import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getStoredConsent, storeConsent } from "@/lib/cookieConsent";
import { loadMarketingScripts } from "@/lib/marketingScripts";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => getStoredConsent());
  // Painel de preferências pode abrir independente da barra ter sido decidida
  // (ex.: link "Preferências de cookies" no rodapé, depois de anos decidido).
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Carrega os scripts de analytics/marketing sempre que o consentimento
  // permitir — cobre tanto quem já decidiu numa visita anterior (roda no
  // boot) quanto o momento em que a pessoa aceita agora.
  useEffect(() => {
    if (consent?.analyticsMarketing) loadMarketingScripts();
  }, [consent?.analyticsMarketing]);

  const acceptAll = useCallback(() => {
    setConsent(storeConsent({ analyticsMarketing: true }));
    setPreferencesOpen(false);
  }, []);

  const acceptNecessaryOnly = useCallback(() => {
    setConsent(storeConsent({ analyticsMarketing: false }));
    setPreferencesOpen(false);
  }, []);

  const savePreferences = useCallback((prefs) => {
    setConsent(storeConsent(prefs));
    setPreferencesOpen(false);
  }, []);

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = useMemo(
    () => ({
      consent,
      decided: consent !== null,
      preferencesOpen,
      acceptAll,
      acceptNecessaryOnly,
      savePreferences,
      openPreferences,
      closePreferences,
    }),
    [consent, preferencesOpen, acceptAll, acceptNecessaryOnly, savePreferences, openPreferences, closePreferences]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

/** Usar dentro de <CookieConsentProvider> (montado em App.js). */
export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent precisa estar dentro de <CookieConsentProvider>");
  return ctx;
}
